import React, { useMemo } from "react";
import moment from "moment";
import { Button, Icon, Text } from "@citifyd/style";

import {
  AdminGetAccountPermissionUserInvitationsQuery,
  AdminGetAccountPermissionUserInvitationsQueryVariables,
  useAdminDeleteAccountPermissionUserInvitationMutation,
} from "../../../graphql/generated/graphql";

import { Pagination } from "../Pagination/Pagination";
import Table from "../../../shared/react/components/Table";

import ApolloClient from "../../../shared/services/apolloClient";
import { usePagination } from "../Pagination/usePagination";
import { useTranslator } from "../../../shared/react/hooks";
import { getService } from "../../../shared/react/utils";
import { useAuthentication } from "../../../shared/react/contexts/authentication";

import styles from "../AccountPermissions.module.scss";
import ListToSeeMore from "../../../shared/react/components/ListToSeeMore";

const apolloClient = ApolloClient();

interface PendingPermissionInvitationsProps {
  loading: boolean;
  pendingPermissions?: AdminGetAccountPermissionUserInvitationsQuery["getAccountPermissionUserInvitations"];
  pageSize: number;
  refetch: (
    variables: AdminGetAccountPermissionUserInvitationsQueryVariables
  ) => any;
}

export const PendingPermissionCompanies = ({
  pendingPermissions,
  refetch,
  loading,
  pageSize,
}: PendingPermissionInvitationsProps) => {
  const t = useTranslator();
  const { user } = useAuthentication();

  const { hasNextPage, hasPreviousPage, nextPage, previousPage } =
    usePagination({
      data: pendingPermissions,
      pageSize,
      refetch,
    });

  const editPermission = (id) => {
    const $state = getService("$state");

    $state.transitionTo(
      "attendant-monitoring-company-edit",
      { id, pending: true },
      { enableBackLink: true }
    );
  };

  const [deletePermissionInternalUser] =
    useAdminDeleteAccountPermissionUserInvitationMutation({
      client: apolloClient,
    });

  const deleteInternalUser = async ({ inviteId }: { inviteId: number }) => {
    if (window.confirm(t("commonConfirmations.areYouSure"))) {
      await deletePermissionInternalUser({
        variables: {
          organizationId: user.organization.id,
          inviteId,
        },
      });
      refetch({
        first: pageSize,
        last: null,
        after: null,
        before: null,
      });
    }
  };

  const columns = [
    {
      value: t("accountPermissions.permissionInternalUser.name"),
      uppercase: false,
      key: "name",
      width: "20%",
    },
    {
      value: t("accountPermissions.permissionInternalUser.email"),
      uppercase: false,
      key: "email",
      width: "25%",
    },

    {
      value: t("accountPermissions.permissionCompanies.properties"),
      uppercase: false,
      key: "properties",
      width: "25%",
    },

    {
      value: t("accountPermissions.permissionInternalUser.invitedAt"),
      uppercase: false,
      key: "invitedAt",
      width: "20%",
    },

    {
      value: t("accountPermissions.actions"),
      uppercase: false,
      key: "actions",
      width: "10%",
      textAlign: "right",
    },
  ];

  const data = useMemo(
    () =>
      pendingPermissions?.edges.map((edge) => {
        const propertyNames = edge.node.properties?.map(
          (property) => property?.name
        );
        return {
          ...edge.node,
          name: {
            value: edge.node.name,
          },
          email: {
            value: edge.node.email,
          },

          properties: {
            value: <ListToSeeMore items={propertyNames} limit={1} />,
          },
          invitedAt: {
            value: moment(edge.node.invitedAt).format("lll"),
          },
          actions: {
            textAlign: "right",
            value: (
              <>
                <Button
                  justifyContent="center"
                  appearance="transparent"
                  onClick={() => editPermission(edge.node.id)}
                >
                  <Icon icon="pencil-alt" />
                </Button>

                <Button
                  justifyContent="center"
                  appearance="transparent"
                  onClick={() => {
                    deleteInternalUser({ inviteId: edge.node.id });
                  }}
                >
                  <Icon icon="trash" />
                </Button>
              </>
            ),
          },
        };
      }),
    [pendingPermissions?.edges]
  );

  return (
    <div>
      <div className={styles.divisor}></div>
      <Text weight="300" variant="h5" gutterBottom>
        {t("accountPermissions.acceptanceInvitations")}
      </Text>

      <Table
        headerAppearance="transparent"
        spacing
        columns={columns}
        data={data}
        responsive
      />

      {(hasNextPage || hasPreviousPage) && (
        <Pagination
          loading={loading}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          nextPage={nextPage}
          previousPage={previousPage}
          data={pendingPermissions?.edges}
        />
      )}
    </div>
  );
};
